import { useMemo, useState } from "react";
import {
  Col,
  Form,
  Row,
  Select,
  Button,
  Space,
  Modal,
  notification,
  Skeleton,
  Typography,
  Divider,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { UserRoleSelectOptions } from "admin/constants";
import {
  useUserSelectOptions,
  useTenantUserSelectOptions,
  useUpdateOrganizationRole,
  useRemoveOrganizationUserMutation,
  useAssignUserToOrganization,
} from "admin/queries";
import "./UserManagement.scss";

export const UserManagement = () => {
  const [form] = Form.useForm();
  const [addForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [api, notificationContextHolder] = notification.useNotification();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const roleOptions = UserRoleSelectOptions();
  const { data: users, isLoading } = useUserSelectOptions();
  const { data: tenantUsers } = useTenantUserSelectOptions();
  const updateOrgRole = useUpdateOrganizationRole();
  const removeUser = useRemoveOrganizationUserMutation();
  const assignUser = useAssignUserToOrganization();

  // Tenant users not already in the organization
  const availableTenantUsers = useMemo(() => {
    if (!tenantUsers || !users) return [];
    const orgUserIds = new Set(users.map((u) => u.value));
    return tenantUsers.filter((tu) => !orgUserIds.has(tu.value));
  }, [tenantUsers, users]);

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    const user = users?.find((u) => u.value === userId);
    if (user) {
      form.setFieldsValue({ organizationRole: user.role || "" });
    }
  };

  const handleAddUser = async () => {
    try {
      const values = await addForm.validateFields();
      await assignUser.mutateAsync({
        userId: values.addUserId,
        role: values.addUserRole,
      });
      addForm.resetFields();
      api.success({ duration: 3, message: "User Added to Organization" });
    } catch (error: any) {
      if (error?.message) {
        api.error({ message: "Error", description: error.message });
      }
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUserId) return;
    try {
      await updateOrgRole.mutateAsync({
        userId: selectedUserId,
        role: form.getFieldValue("organizationRole"),
      });
      api.success({ duration: 3, message: "Role Updated" });
    } catch (error: any) {
      api.error({ message: "Error", description: error?.message });
    }
  };

  const handleDelete = () => {
    if (!selectedUserId) return;
    modal.confirm({
      title: "Remove User",
      icon: <ExclamationCircleOutlined />,
      content:
        "This removes the user's access to this organization. Their notes and history are kept.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await removeUser.mutateAsync(selectedUserId);
          setSelectedUserId(null);
          form.resetFields();
          api.success({ duration: 3, message: "User Removed" });
        } catch (error: any) {
          api.error({ message: "Error", description: error?.message });
        }
      },
    });
  };

  if (isLoading) return <Skeleton active />;

  return (
    <div className="user-management">
      {notificationContextHolder}
      {contextHolder}
      <Typography.Title className="user-management-title">User Management</Typography.Title>
      <Typography.Paragraph type="secondary">Manage user roles and access within this organization.</Typography.Paragraph>

      <Typography.Title level={5}>Add User to Organization</Typography.Title>
      <Typography.Paragraph type="secondary">
        Select a tenant member to add to this organization with a role.
      </Typography.Paragraph>
      <Form form={addForm} layout="vertical">
        <Row gutter={20}>
          <Col span={8}>
            <Form.Item name="addUserId" label="Select User" rules={[{ required: true, message: "Select a user" }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select a tenant user..."
                options={availableTenantUsers.map((u) => ({ value: u.value, label: u.label }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="addUserRole" label="Organization Role" rules={[{ required: true, message: "Select a role" }]}>
              <Select
                placeholder="Select a role..."
                options={roleOptions}
              />
            </Form.Item>
          </Col>
          <Col span={8} style={{ display: "flex", alignItems: "end", paddingBottom: 24 }}>
            <Button
              type="primary"
              loading={assignUser.isPending}
              onClick={handleAddUser}
            >
              Add User
            </Button>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Typography.Title level={5}>Update User Role</Typography.Title>
      <Typography.Paragraph type="secondary">
        Select a user to update their organization role or remove them from the
        organization.
      </Typography.Paragraph>
      <Form form={form} layout="vertical">
        <Row gutter={20}>
          <Col span={8}>
            <Form.Item name="user" label="Select User">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select a user..."
                options={users?.map((u) => ({ value: u.value, label: u.label }))}
                onChange={handleUserSelect}
              />
            </Form.Item>
          </Col>
          {selectedUserId && (
            <Col span={8}>
              <Form.Item name="organizationRole" label="Organization Role">
                <Select
                  options={[{ value: "", label: "Select..." }, ...roleOptions]}
                />
              </Form.Item>
              <Space>
                <Button
                  type="primary"
                  loading={updateOrgRole.isPending}
                  onClick={handleUpdateRole}
                >
                  Update Role
                </Button>
                <Button
                  danger
                  loading={removeUser.isPending}
                  onClick={handleDelete}
                >
                  Remove User
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
};
